import { useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Field } from "@/components/ds";
import { ResponsiveModal } from "@/components/ds/ResponsiveModal";
import { NativeSelect } from "@/components/ds/NativeSelect";
import { NOTE_COLORS } from "./noteUtils";

/** Cadastro e edição do recado. Quem cadastrou e a conclusão são registrados pelo backend. */
export function NoteModal({ note, onClose, onSaved }) {
  const [form, setForm] = useState({
    title: note?.title || "",
    description: note?.description || "",
    due_date: note?.due_date || "",
    assignee: note?.assignee || "",
    notes: note?.notes || "",
    reference: note?.reference || "",
    color: note?.color || "AMARELO",
    priority: note?.priority || "NORMAL",
  });
  const [busy, setBusy] = useState(false);
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (note) await api.put(`/notes/${note.id}`, form);
      else await api.post("/notes", form);
      toast.success(note ? "Recado atualizado." : "Recado fixado no mural.");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <ResponsiveModal
      open
      onOpenChange={onClose}
      title={note ? "Editar recado" : "Novo recado"}
      description={note ? note.title : "O que não pode ser esquecido?"}
      testid="note-modal"
    >
      <form onSubmit={submit} className="space-y-4">
        <Field label="Título *">
          <Input
            value={form.title}
            onChange={set("title")}
            required
            placeholder="Retornar orçamento da Maria"
            data-testid="note-title-input"
          />
        </Field>
        <Field label="Mensagem / descrição *">
          <Textarea
            value={form.description}
            onChange={set("description")}
            required
            rows={3}
            placeholder="Pediu orçamento de 20 chaveiros personalizados pelo Instagram."
            data-testid="note-description-input"
          />
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Prazo *">
            <Input
              type="date"
              value={form.due_date}
              onChange={set("due_date")}
              required
              data-testid="note-due-input"
            />
          </Field>
          <Field label="Responsável *">
            <Input
              value={form.assignee}
              onChange={set("assignee")}
              required
              placeholder="Ângela"
              data-testid="note-assignee-input"
            />
          </Field>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Cliente, contato ou pedido" hint="Opcional">
            <Input
              value={form.reference}
              onChange={set("reference")}
              placeholder="PED-00009 ou Maria do Instagram"
              data-testid="note-reference-input"
            />
          </Field>
          <Field label="Prioridade">
            <NativeSelect
              value={form.priority}
              onChange={(v) => setForm((f) => ({ ...f, priority: v }))}
              options={[
                { value: "NORMAL", label: "Normal" },
                { value: "URGENTE", label: "Urgente" },
              ]}
              data-testid="note-priority-select"
            />
          </Field>
        </div>
        <Field label="Cor do post-it">
          <div className="flex gap-2">
            {Object.entries(NOTE_COLORS).map(([key, color]) => (
              <button
                key={key}
                type="button"
                onClick={() => setForm((f) => ({ ...f, color: key }))}
                data-testid={`note-color-${key.toLowerCase()}`}
                aria-label={color.label}
                className={`h-9 w-9 rounded-lg border-2 transition-transform hover:scale-105 ${color.swatch} ${
                  form.color === key ? "border-brand" : "border-transparent"
                }`}
              />
            ))}
          </div>
        </Field>
        <Field label="Observações" hint="Opcional">
          <Textarea
            value={form.notes}
            onChange={set("notes")}
            rows={2}
            placeholder="Detalhes que ajudam no atendimento"
            data-testid="note-notes-input"
          />
        </Field>
        <div className="flex gap-2 pt-1">
          <Button type="button" variant="outline" onClick={onClose} className="flex-1 rounded-full"
                  data-testid="note-cancel-btn">
            Cancelar
          </Button>
          <Button type="submit" disabled={busy} className="flex-1 rounded-full"
                  data-testid="note-save-btn">
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {note ? "Salvar alterações" : "Fixar no mural"}
          </Button>
        </div>
      </form>
    </ResponsiveModal>
  );
}
